import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowRight, BookOpen, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/Skeleton";
import { cn } from "@/lib/utils";
import { useDocumentTitle } from "@/lib/useDocumentTitle";

const MODULE_ACCENT = {
    gottman: "from-rose-100 to-amber-50 border-rose-200",
    act:     "from-emerald-100 to-teal-50 border-emerald-200",
    dbt:     "from-violet-100 to-fuchsia-50 border-violet-200",
    cbt:     "from-sky-100 to-blue-50 border-sky-200",
    eft:     "from-pink-100 to-rose-50 border-pink-200",
    mi:      "from-amber-100 to-orange-50 border-amber-200",
};

export default function LearnModulesPage() {
    const [modules, setModules] = useState(null);
    useDocumentTitle("Learn");

    useEffect(() => {
        let cancelled = false;
        api.get("/learn/modules")
            .then((r) => { if (!cancelled) setModules(r.data); })
            .catch(() => {
                if (cancelled) return;
                toast.error("Could not load learn modules");
                setModules([]);
            });
        return () => { cancelled = true; };
    }, []);

    const totals = React.useMemo(() => {
        if (!modules) return null;
        const chapters = modules.reduce((s, m) => s + m.chapter_count, 0);
        const completed = modules.reduce((s, m) => s + m.chapters_completed, 0);
        return { chapters, completed };
    }, [modules]);

    return (
        <Layout>
            <div className="space-y-8 animate-fade-in-up" data-testid="learn-modules-page">
                <header>
                    <p className="text-sm font-medium text-primary">Learn</p>
                    <h1 className="mt-1 text-3xl font-semibold tracking-tight text-foreground sm:text-4xl">
                        Method library
                    </h1>
                    <p className="mt-3 max-w-2xl text-base text-muted-foreground">
                        Six therapy methods, each broken into short chapters with a quiz at the end.
                        Choose a method to start — or pick up where you left off.
                    </p>
                </header>

                {totals && (
                    <Card className="border-border/80 shadow-sm" data-testid="learn-modules-overall">
                        <CardContent className="flex flex-wrap items-baseline justify-between gap-3 p-5">
                            <p className="text-sm text-foreground">
                                <span
                                    className="text-xl font-semibold tabular-nums"
                                    data-testid="learn-modules-overall-completed"
                                >
                                    {totals.completed}
                                </span>{" "}
                                <span className="text-muted-foreground">
                                    of {totals.chapters} chapters complete across all methods
                                </span>
                            </p>
                            <p className="text-xs text-muted-foreground">
                                {modules.length} methods · {totals.chapters} chapters total
                            </p>
                        </CardContent>
                    </Card>
                )}

                {!modules ? (
                    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                        {Array.from({ length: 6 }).map((_, i) => (
                            <Skeleton key={i} className="h-44 w-full rounded-2xl" />
                        ))}
                    </div>
                ) : (
                    <ul
                        className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3"
                        data-testid="learn-modules-grid"
                    >
                        {modules.map((m) => {
                            const pct = m.chapter_count
                                ? Math.round((m.chapters_completed / m.chapter_count) * 100)
                                : 0;
                            const accent = MODULE_ACCENT[m.slug] || "from-slate-100 to-slate-50 border-slate-200";
                            const done = m.chapters_completed === m.chapter_count && m.chapter_count > 0;
                            return (
                                <li key={m.slug}>
                                    <Link
                                        to={`/learn/${m.slug}`}
                                        data-testid={`learn-module-card-${m.slug}`}
                                        className="block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring rounded-2xl"
                                    >
                                        <Card
                                            className={cn(
                                                "group h-full overflow-hidden border bg-gradient-to-br shadow-sm transition-all",
                                                "hover:-translate-y-0.5 hover:shadow-md",
                                                accent
                                            )}
                                        >
                                            <CardContent className="flex h-full flex-col gap-4 p-5">
                                                <div className="flex items-start justify-between gap-3">
                                                    <div className="space-y-1">
                                                        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                                                            Method
                                                        </p>
                                                        <h3
                                                            className="text-xl font-semibold tracking-tight text-foreground"
                                                            data-testid={`learn-module-name-${m.slug}`}
                                                        >
                                                            {m.name}
                                                        </h3>
                                                    </div>
                                                    {done && (
                                                        <span
                                                            className="inline-flex items-center gap-1 rounded-full border border-emerald-300 bg-white/70 px-2 py-0.5 text-xs font-medium text-emerald-700"
                                                            data-testid={`learn-module-done-${m.slug}`}
                                                        >
                                                            <CheckCircle2 className="h-3.5 w-3.5" />
                                                            Complete
                                                        </span>
                                                    )}
                                                </div>
                                                <p className="text-sm leading-relaxed text-foreground/80">
                                                    {m.subtitle}
                                                </p>
                                                <div className="mt-auto space-y-2">
                                                    <div className="flex items-baseline justify-between text-xs">
                                                        <span className="font-medium text-foreground tabular-nums">
                                                            <span data-testid={`learn-module-completed-${m.slug}`}>
                                                                {m.chapters_completed}
                                                            </span>
                                                            {" / "}
                                                            <span data-testid={`learn-module-count-${m.slug}`}>
                                                                {m.chapter_count}
                                                            </span>{" "}
                                                            <span className="text-muted-foreground">chapters</span>
                                                        </span>
                                                        {m.user_average_score != null && (
                                                            <span
                                                                className="text-muted-foreground"
                                                                data-testid={`learn-module-avg-${m.slug}`}
                                                            >
                                                                avg {Math.round(m.user_average_score)}%
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div className="h-1.5 w-full overflow-hidden rounded-full bg-white/60">
                                                        <div
                                                            className="h-full rounded-full bg-foreground/70 transition-all duration-700"
                                                            style={{ width: `${pct}%` }}
                                                            data-testid={`learn-module-bar-${m.slug}`}
                                                        />
                                                    </div>
                                                    <div className="flex items-center justify-between pt-1 text-xs">
                                                        <span className="inline-flex items-center gap-1 text-muted-foreground">
                                                            <BookOpen className="h-3 w-3" />
                                                            Open module
                                                        </span>
                                                        <ArrowRight className="h-4 w-4 text-foreground transition-transform group-hover:translate-x-1" />
                                                    </div>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    </Link>
                                </li>
                            );
                        })}
                    </ul>
                )}
            </div>
        </Layout>
    );
}
